import { useTranslation } from '../utils/useTranslation';
import { ArrowLeft, Calendar, Clock, User } from 'lucide-react';

interface BlogProps {
    onBack: () => void;
    onNavigate: (view: 'blog-post-1' | 'blog-post-2' | 'blog-post-3' | 'blog-post-4' | 'blog-post-5' | 'blog-post-6') => void;
}

export function Blog({ onBack, onNavigate }: BlogProps) {
    const { t } = useTranslation();

    const blogPosts = [
        {
            id: 1,
            title: t('blog.posts.post1.title'),
            excerpt: t('blog.posts.post1.excerpt'),
            date: '2025-11-20',
            readTime: '10 min',
            author: 'Mystery Gift Team',
            image: 'https://images.unsplash.com/photo-1513885535751-8b9238bd345a?auto=format&fit=crop&q=80',
            category: t('blog.categories.tips')
        },
        {
            id: 2,
            title: t('blog.posts.post2.title'),
            excerpt: t('blog.posts.post2.excerpt'),
            date: '2025-11-15',
            readTime: '8 min',
            author: 'Mystery Gift Team',
            image: 'https://images.unsplash.com/photo-1512418490979-92798cec1380?auto=format&fit=crop&q=80',
            category: t('blog.categories.features')
        },
        {
            id: 3,
            title: t('blog.posts.post3.title'),
            excerpt: t('blog.posts.post3.excerpt'),
            date: '2025-11-10',
            readTime: '15 min',
            author: 'Mystery Gift Team',
            image: 'https://images.unsplash.com/photo-1513201099705-a9746e1e201f?auto=format&fit=crop&q=80',
            category: t('blog.categories.tips')
        },
        {
            id: 4,
            title: t('blog.posts.post4.title'),
            excerpt: t('blog.posts.post4.excerpt'),
            date: '2025-11-05',
            readTime: '18 min',
            author: 'Mystery Gift Team',
            image: 'https://images.unsplash.com/photo-1511578314322-379afb476865?auto=format&fit=crop&q=80',
            category: t('blog.categories.design')
        },
        {
            id: 5,
            title: t('blog.posts.post5.title'),
            excerpt: t('blog.posts.post5.excerpt'),
            date: '2025-11-01',
            readTime: '12 min',
            author: 'Mystery Gift Team',
            image: 'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?auto=format&fit=crop&q=80',
            category: t('blog.categories.technology')
        },
        {
            id: 6,
            title: t('blog.posts.post6.title'),
            excerpt: t('blog.posts.post6.excerpt'),
            date: '2025-10-28',
            readTime: '14 min',
            author: 'Mystery Gift Team',
            image: 'https://images.unsplash.com/photo-1588196749597-9ff075ee6b5b?auto=format&fit=crop&q=80',
            category: t('blog.categories.trends')
        }
    ];

    return (
        <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-green-50 pt-24 pb-12">
            <div className="container mx-auto px-4 max-w-6xl">
                {/* Header */}
                <div className="mb-8">
                    <button
                        onClick={onBack}
                        className="flex items-center gap-2 text-gray-600 hover:text-red-600 transition-colors mb-6"
                    >
                        <ArrowLeft className="w-5 h-5" />
                        {t('blog.back')}
                    </button>

                    <div className="text-center mb-12">
                        <h1 className="text-5xl font-bold bg-gradient-to-r from-red-600 to-green-600 bg-clip-text text-transparent mb-4">
                            📝 {t('blog.title')}
                        </h1>
                        <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                            {t('blog.subtitle')}
                        </p>
                    </div>
                </div>

                {/* Featured Post */}
                <div className="mb-12">
                    <div
                        onClick={() => onNavigate('blog-post-1')}
                        className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl overflow-hidden border border-white/20 hover:shadow-2xl transition-all duration-300 cursor-pointer group"
                    >
                        <div className="md:flex">
                            <div className="md:w-1/3 relative overflow-hidden group-hover:scale-105 transition-transform duration-500">
                                <img
                                    src={blogPosts[0].image}
                                    alt={blogPosts[0].title}
                                    className="w-full h-full object-cover"
                                />
                                <div className="absolute inset-0 bg-gradient-to-br from-red-500/50 to-green-500/50"></div>
                            </div>
                            <div className="md:w-2/3 p-8">
                                <div className="flex items-center gap-3 mb-4">
                                    <span className="px-3 py-1 bg-red-100 text-red-600 rounded-full text-sm font-medium">
                                        {t('blog.featured')}
                                    </span>
                                    <span className="px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-sm">
                                        {blogPosts[0].category}
                                    </span>
                                </div>
                                <h2 className="text-3xl font-bold text-gray-800 mb-4 group-hover:text-red-600 transition-colors">
                                    {blogPosts[0].title}
                                </h2>
                                <p className="text-gray-600 mb-6 text-lg">
                                    {blogPosts[0].excerpt}
                                </p>
                                <div className="flex items-center gap-6 text-sm text-gray-500">
                                    <div className="flex items-center gap-2">
                                        <Calendar className="w-4 h-4" />
                                        {blogPosts[0].date}
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Clock className="w-4 h-4" />
                                        {blogPosts[0].readTime}
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <User className="w-4 h-4" />
                                        {blogPosts[0].author}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Blog Grid */}
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {blogPosts.slice(1).map((post) => (
                        <div
                            key={post.id}
                            onClick={() => {
                                if (post.id === 2) onNavigate('blog-post-2');
                                if (post.id === 3) onNavigate('blog-post-3');
                                if (post.id === 4) onNavigate('blog-post-4');
                                if (post.id === 5) onNavigate('blog-post-5');
                                if (post.id === 6) onNavigate('blog-post-6');
                            }}
                            className="bg-white/80 backdrop-blur-sm rounded-xl shadow-lg overflow-hidden border border-white/20 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 cursor-pointer"
                        >
                            <div className="relative h-48 overflow-hidden">
                                <img
                                    src={post.image}
                                    alt={post.title}
                                    className="w-full h-full object-cover"
                                />
                                <div className="absolute inset-0 bg-gradient-to-br from-purple-500/50 to-pink-500/50"></div>
                            </div>
                            <div className="p-6">
                                <span className="px-3 py-1 bg-purple-100 text-purple-600 rounded-full text-xs font-medium">
                                    {post.category}
                                </span>
                                <h3 className="text-xl font-bold text-gray-800 mt-3 mb-2">
                                    {post.title}
                                </h3>
                                <p className="text-gray-600 mb-4 line-clamp-3">
                                    {post.excerpt}
                                </p>
                                <div className="flex items-center gap-4 text-xs text-gray-500">
                                    <div className="flex items-center gap-1">
                                        <Calendar className="w-3 h-3" />
                                        {post.date}
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <Clock className="w-3 h-3" />
                                        {post.readTime}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Newsletter Section */}
                <div className="mt-16 bg-gradient-to-r from-red-500 to-green-500 rounded-2xl p-8 md:p-12 text-white text-center">
                    <h2 className="text-3xl font-bold mb-4">
                        📬 {t('blog.newsletter.title')}
                    </h2>
                    <p className="text-lg mb-6 opacity-90">
                        {t('blog.newsletter.subtitle')}
                    </p>
                    <div className="max-w-md mx-auto flex gap-3">
                        <input
                            type="email"
                            placeholder={t('blog.newsletter.placeholder')}
                            className="flex-1 px-4 py-3 rounded-lg text-gray-800 focus:outline-none focus:ring-2 focus:ring-white"
                        />
                        <button className="px-6 py-3 bg-white text-red-600 font-semibold rounded-lg hover:bg-gray-100 transition-colors">
                            {t('blog.newsletter.button')}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
